import { createContext, useState, useContext } from 'react'
import { differenceBy } from 'lodash'

import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import { ToastType, ToastPosition } from '@hotmart/space-lib-events/dist/toast/enum'

import { getStatusMedia } from 'src/client/publication'
import i18n from 'src/i18n'
import { StatusMedia } from 'src/client/publication/types'

import { CHECK_POST_STATUS_INTERVAL_KEY, MEDIAS_PROCESSING_KEY } from 'src/utils/constants'
import { PostProps, PostStatus } from '.'

type PropsPosts = {
  currentPage: number
  loading: boolean
  postSelected: PostProps
  postsProcessing: Array<PostProps>
  refreshPage: boolean
  myPostsChecked: boolean
  setLoading: (loading: boolean) => void
  setRefreshPage: (status: boolean) => void
  setPostSelected: (postSelected: PostProps) => void
  setCurrentPage: (currentPage: number) => void
  setPostsProcessing: (postsProcessing: Array<PostProps>) => void
  nextPage: () => void
  setMyPostsChecked: (myPostsChecked: boolean) => void
  startCheckPostStatus: (posts?: PostProps[]) => void
}

const PostsContext = createContext({} as PropsPosts)

const PostsProvider = ({ children }) => {
  const itemsLocalStorage = localStorage.getItem(MEDIAS_PROCESSING_KEY)
  const mediasInProcessing = JSON.parse(itemsLocalStorage || '[]') as Array<PostProps>

  const [loading, setLoading] = useState(true)
  const [refreshPage, setRefreshPage] = useState(true)
  const [myPostsChecked, setMyPostsChecked] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [postSelected, setPostSelected] = useState<PostProps>({} as PostProps)
  const [postsProcessing, setPostsProcessing] = useState<Array<PostProps>>(mediasInProcessing)
  const [checkPostStatusIntervalId, setCheckPostStatusIntervalId] = useState<NodeJS.Timeout>()

  function nextPage() {
    setCurrentPage((prevState) => prevState + 1)
  }

  const getLocalPostInProgress = () => {
    const itemsLocalStorage = localStorage.getItem(MEDIAS_PROCESSING_KEY)
    return JSON.parse(itemsLocalStorage || '[]') as Array<PostProps>
  }

  const startCheckPostStatus = () => {
    const mediasInProcessing = getLocalPostInProgress()

    if (mediasInProcessing.length && !checkPostStatusIntervalId) {
      const intervalId = setInterval(async () => {
        const currentLocalStorage = getLocalPostInProgress()

        if (currentLocalStorage.length) {
          const response = await getStatusMedia(currentLocalStorage)

          const processedPosts = response.filter(
            (post: StatusMedia) =>
              post.status === PostStatus.ACTIVE || post.status === PostStatus.SCHEDULED
          )

          const processedErrorsPosts = response.filter(
            (post: StatusMedia) =>
              ![PostStatus.ACTIVE, PostStatus.PROCESSING, PostStatus.SCHEDULED].includes(
                post.status
              )
          )

          const currentProcessingPost = differenceBy(currentLocalStorage, processedPosts, 'id')

          if (processedPosts.length) {
            localStorage.setItem(MEDIAS_PROCESSING_KEY, JSON.stringify(currentProcessingPost))
            setPostsProcessing(currentProcessingPost)
            setCurrentPage(0)
            setRefreshPage(true)
            toastEventDispatch({
              type: ToastType.SUCCESS,
              message: i18n.t('toasts.publish_success'),
              position: ToastPosition.BOTTOM_RIGHT
            })
          }

          if (processedErrorsPosts.length) {
            toastEventDispatch({
              type: ToastType.DANGER,
              message: i18n.t('toasts.error_in_publish'),
              position: ToastPosition.BOTTOM_RIGHT
            })
          }
        }

        if (!currentLocalStorage.length) {
          const intervalId = localStorage.getItem(CHECK_POST_STATUS_INTERVAL_KEY)

          if (intervalId) {
            clearInterval(Number(intervalId))
            localStorage.removeItem(CHECK_POST_STATUS_INTERVAL_KEY)
          }
        }
      }, 10000)

      localStorage.setItem(CHECK_POST_STATUS_INTERVAL_KEY, JSON.stringify(intervalId))
      setCheckPostStatusIntervalId(intervalId)
    }

    if (!mediasInProcessing.length) {
      localStorage.removeItem(CHECK_POST_STATUS_INTERVAL_KEY)
    }
  }

  return (
    <PostsContext.Provider
      value={{
        currentPage,
        loading,
        postSelected,
        postsProcessing,
        refreshPage,
        myPostsChecked,
        setLoading,
        setRefreshPage,
        setPostSelected,
        setCurrentPage,
        setPostsProcessing,
        nextPage,
        setMyPostsChecked,
        startCheckPostStatus
      }}
    >
      {children}
    </PostsContext.Provider>
  )
}

function usePosts() {
  const context = useContext(PostsContext)

  if (!Object.keys(context).length) {
    throw new Error('usePosts must be used within a PostsProvider')
  }

  return context
}

export { PostsProvider, usePosts }
